import { isAllowedRuntimeValue } from "./runtime-catalog.ts";
import type { RuntimeOverrideField } from "./runtime-catalog.ts";

export const RUNTIME_OVERRIDES_KEY = "devtools:runtime-overrides:v1";

const ALLOWLIST: RuntimeOverrideField[] = ["backend", "gateway", "embassy"];

let runtimeDefaults: RuntimeSettings | null = null;

export function captureRuntimeDefaults(runtime: RuntimeSettings): void {
  runtimeDefaults = { ...runtime };
}

export function getRuntimeDefaults(): RuntimeSettings | null {
  return runtimeDefaults ? { ...runtimeDefaults } : null;
}

function isOverrideEnvironment(
  environment: RuntimeSettings["environment"]
): boolean {
  return environment === "staging" || environment === "development";
}

function getLocalStorage(): Storage | null {
  if (typeof globalThis.localStorage === "undefined") {
    return null;
  }
  return globalThis.localStorage;
}

function readStoredOverrides(): Partial<
  Record<RuntimeOverrideField, string>
> | null {
  const storage = getLocalStorage();
  if (!storage) return null;
  const raw = storage.getItem(RUNTIME_OVERRIDES_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: Partial<Record<RuntimeOverrideField, string>> = {};
    for (const key of ALLOWLIST) {
      const value = parsed[key];
      if (value === undefined) continue;
      if (typeof value !== "string" || !isAllowedRuntimeValue(key, value)) {
        storage.removeItem(RUNTIME_OVERRIDES_KEY);
        return null;
      }
      result[key] = value;
    }
    if (Object.keys(result).length === 0) {
      return null;
    }
    return result;
  } catch {
    storage.removeItem(RUNTIME_OVERRIDES_KEY);
    return null;
  }
}

export function hasActiveRuntimeOverrides(
  environment: RuntimeSettings["environment"]
): boolean {
  if (!isOverrideEnvironment(environment)) return false;
  return readStoredOverrides() !== null;
}

export function applyRuntimeOverrides(
  runtime: RuntimeSettings
): RuntimeSettings {
  if (!isOverrideEnvironment(runtime.environment)) {
    return runtime;
  }
  const stored = readStoredOverrides();
  if (!stored) return runtime;
  return {
    ...runtime,
    ...stored
  };
}

export function saveRuntimeOverrides(
  environment: RuntimeSettings["environment"],
  overrides: Partial<Record<RuntimeOverrideField, string>>
): boolean {
  if (!isOverrideEnvironment(environment)) return false;
  for (const key of ALLOWLIST) {
    const value = overrides[key];
    if (value !== undefined && !isAllowedRuntimeValue(key, value)) {
      return false;
    }
  }
  const payload: Partial<Record<RuntimeOverrideField, string>> = {};
  for (const key of ALLOWLIST) {
    const value = overrides[key];
    if (value !== undefined) {
      payload[key] = value;
    }
  }
  const storage = getLocalStorage();
  if (!storage) return false;
  storage.setItem(RUNTIME_OVERRIDES_KEY, JSON.stringify(payload));
  return true;
}

export function clearRuntimeOverrides(
  environment: RuntimeSettings["environment"]
): void {
  if (!isOverrideEnvironment(environment)) return;
  getLocalStorage()?.removeItem(RUNTIME_OVERRIDES_KEY);
}

export function deepFreeze<T extends object>(value: T): T {
  Object.freeze(value);
  for (const child of Object.values(value)) {
    if (child && typeof child === "object" && !Object.isFrozen(child)) {
      deepFreeze(child as object);
    }
  }
  return value;
}
